import { FC, useContext, useMemo } from 'react';

import { useApolloClient } from '@apollo/client';
import { Label, Separator, Tooltip } from '@smartshell/ui-kit';
import cn from 'classnames';

import { LocalizationContext } from 'localization';
import { SettingsContext } from 'settings';
import { getLabelProps } from 'utils/getLabelProps';
import { checkIsMultipleDays } from 'utils/tariff';

import { ITariffCardProps } from './interfaces';
import s from './TariffCard.module.scss';
import { renderGroups, formatDuration, renderSchedule } from './utils';

export const TariffCard: FC<ITariffCardProps> = ({
  tariff,
  fullHeight,
  host_group_id,
  onClick,
  preview,
}) => {
  const { t } = useContext(LocalizationContext);
  const {
    state: {
      settings: { currency },
    },
  } = useContext(SettingsContext);
  const isMultipleDays = useMemo(
    () => checkIsMultipleDays(tariff.hours.start, tariff.hours.end),
    [tariff.hours.start, tariff.hours.end]
  );

  const client = useApolloClient();

  const labelProps = getLabelProps(tariff.type);

  return (
    <div
      className={cn(
        s.container,
        fullHeight && s.fullHeight,
        preview && s.preview
      )}
      onClick={onClick}
    >
      <div className={cn(s.header, s[tariff.type])}>
        <Label {...labelProps} text={t(labelProps.locale)} />
      </div>
      <div>
        {tariff.title ? (
          <span className={s.title}>{tariff.title}</span>
        ) : (
          <span className={s.gray}>{t('title')}</span>
        )}

        <div className={s.labels}>
          {isMultipleDays && (
            <Tooltip content={t('tariff.night')} contentClassName={s.tooltip}>
              <Label iconName="NightIcon" rounded />
            </Tooltip>
          )}
          {tariff.use_global_discounts && (
            <Tooltip
              content={t('tariff.discount.canApply')}
              contentClassName={s.tooltip}
            >
              <Label iconName="PercentIcon" rounded />
            </Tooltip>
          )}
          {!tariff.show_in_billing && (
            <Tooltip
              content={t('tariff.billing.visible_2')}
              contentClassName={s.tooltip}
            >
              <Label iconName="BuyNoIcon" color="gray-primary" rounded />
            </Tooltip>
          )}
          {!tariff.show_in_shell && (
            <Tooltip
              content={t('group.table.show_in_shell.title')}
              contentClassName={s.tooltip}
            >
              <Label iconName="EyeNoIcon" color="gray-primary" rounded />
            </Tooltip>
          )}
          {tariff.show_in_shell && tariff.highlighted && (
            <Tooltip
              content={t('group.table.show_in_shell.title')}
              contentClassName={s.tooltip}
            >
              <Label iconName="StarsIcon" color="violet" rounded />
            </Tooltip>
          )}
          {tariff.is_tracked && (
            <Tooltip
              content={t('telegram.notify')}
              contentClassName={s.tooltip}
            >
              <Label iconName="PaperPlaneIcon" color="blue" rounded />
            </Tooltip>
          )}
          {tariff.online_booking_enabled && (
            <Tooltip
              content={t('tariff.bookable')}
              contentClassName={s.tooltip}
            >
              <Label iconName="CheckedCalendarIcon" color="blue" rounded />
            </Tooltip>
          )}
          {tariff.use_on_holidays && (
            <Tooltip
              content={t('tariff.isHoliday')}
              contentClassName={s.tooltip}
            >
              <Label iconName="CalendarDayIcon" color="red" rounded />
            </Tooltip>
          )}
        </div>
      </div>
      {renderGroups(t, tariff, currency.sign, client, host_group_id)}
      <Separator className={s['last-separator']} />
      <div className={cn(s.columns, s.bottom)}>
        <div className={s.duration}>
          <span className={s.gray}>{t('duration')}</span>
          {tariff.type === 'PACKET'
            ? t('until', tariff.hours.end)
            : tariff.duration
            ? t(formatDuration(tariff.duration_type), tariff.duration)
            : '—'}
          {tariff.type === 'PAUSABLE' && (
            <div className={s.lifetime}>
              <span className={s.gray}>{t('tariff.lifeTime')}</span>
              {tariff.lifetime
                ? t(formatDuration(tariff.lifetime_type), tariff.lifetime)
                : '—'}
            </div>
          )}
        </div>
        <div>
          <span className={s.gray}>
            {tariff.type === 'FIXED'
              ? t('tariff.schedule_payment')
              : t('tariff.schedule')}
          </span>
          {renderSchedule(tariff)}
        </div>
      </div>
    </div>
  );
};
